/**
 * Copyright IBM Corp. 2021
 *
 * This source code is licensed under the IBM Public License Version 1.0 found in the
 * LICENSE file in the root directory of this source tree.
 */

function getSvgExtents(svgElement) {
  const defaultExtent = { height: 0, width: 0, x: 0, y: 0 };
  if (svgElement) {
    try {
      return svgElement.getBBox();
    } catch (e) {
      return defaultExtent;
    }
  }
  return defaultExtent;
}

function setSvgViewBox(svgElement, x, y, width, height) {
  if (!svgElement) return;
  svgElement.setAttribute("viewBox", `${x} ${y} ${width} ${height}`);
}

function isSvgViewBoxEquals(a, b) {
  return (
    a === b ||
    (a?.scaleX === b?.scaleX &&
      a?.scaleY === b?.scaleY &&
      a?.x === b?.x &&
      a?.y === b?.y)
  );
}

function createInitialSvgViewBox(svgExtents, svgViewPort) {
  return {
    scaleX: svgExtents.width !== 0 ? svgViewPort.width / svgExtents.width : 0,
    scaleY:
      svgExtents.height !== 0 ? svgViewPort.height / svgExtents.height : 0,
    x: svgExtents.x,
    y: svgExtents.y,
  };
}

function computeCurrentSvgViewBoxScale(
  oldSvgViewPort,
  newSvgViewPort,
  svgViewBox
) {
  return {
    x: svgViewBox.x,
    y: svgViewBox.y,
    scaleX: (newSvgViewPort.width * svgViewBox.scaleX) / oldSvgViewPort.width,
    scaleY: (newSvgViewPort.height * svgViewBox.scaleY) / oldSvgViewPort.height,
  };
}

function updateSvgStrokeWidths(svgElement, currentStrokeWidthScale) {
  if (!svgElement) {
    return -1;
  }

  const matrix = svgElement.getScreenCTM();

  const scale = matrix ? matrix.a : "1";

  if (currentStrokeWidthScale === scale) {
    return scale;
  }

  svgElement.querySelectorAll("g [stroke-width]").forEach((element) => {
    let transformScale = 1.0;
    if (element.hasAttribute("sf")) {
      transformScale = parseFloat(element.getAttribute("sf"));
    }

    const strokeWidth = 1.0 / transformScale;
    const newStrokeWidth = strokeWidth / scale;
    element.setAttribute("stroke-width", newStrokeWidth);
  });

  return scale;
}

function createAttachedMap( svgElement, spaceLayer )
{
  if (svgElement === null) {
    return null;
  }

  const attachedMap = new Map();

  if( spaceLayer )
  {
    let layer;
    Array.from( svgElement.children ).forEach( ( child ) =>
    {
      if( child.tagName === "g" && child.id === spaceLayer )
      {
        layer = child;
      }
    } );

    if( !layer ) return attachedMap;

    Array.from( layer.children ).forEach( ( element ) =>
      {
        const recordId = element.getAttribute( "tri-element-name" );
        if( recordId && recordId.length > 0 )
        {
          attachedMap.set( recordId, element );
        }
      } );
  }
  else
  {
    svgElement.querySelectorAll( "[tri-record-id].tri-attached" ).forEach( ( element ) =>
      {
        const recordId = element.getAttribute( "tri-record-id" );
        if( recordId && recordId.length > 0 )
        {
          addRecordToMap( attachedMap, recordId, element )
        }
      } );
  }

  return attachedMap;
}

function createInteractiveMap( svgElement, spaceLayer )
{
  if( svgElement === null ) {
    return null;
  }
  const interactiveMap = new Map();

  if( spaceLayer )
  {
    let layer;
    Array.from( svgElement.children ).forEach( ( child ) =>
    {
      if( child.tagName === "g" && child.id === spaceLayer )
      {
        layer = child;
      }
    } );

    if( !layer ) return interactiveMap;

    Array.from( layer.children ).forEach( ( element ) =>
    {
      const recordId = element.getAttribute( "tri-element-name" );
      if( recordId && recordId.length > 0 )
      {
        interactiveMap.set( recordId, element );
      }
    } );
  }
  else
  {
    svgElement
      .querySelectorAll( "[tri-record-id].tri-interactive" )
      .forEach( ( element ) =>
      {
        const recordId = element.getAttribute( "tri-record-id" );
        addRecordToMap( interactiveMap, recordId, element )
      } );
    }

    return interactiveMap;
}

function addRecordToMap( map, recordId, element )
{
  // When mutiple polylines are bound to the same id, this is typicaly
  // a "hole-on-middle" situation.  Using the one with the largest area
  // gets the real space not one of the holes
  const el = map.get( recordId );
  if( el )
  {
    const bBoxExisting = el.getBBox();
    const bBoxNew = element.getBBox();
    const areaExisting = bBoxExisting.height * bBoxExisting.width;
    const areaNew = bBoxNew.height * bBoxNew.width;
    if( areaNew > areaExisting )
    {
      map.set( recordId, element );
    }
  }
  else
  {
    map.set( recordId, element );
  }
}

function getNormalizedDelta(deltaX, deltaY, matrix, svgElement) {
  const origin = svgElement.createSVGPoint().matrixTransform(matrix);
  let delta = svgElement.createSVGPoint();
  delta.x = deltaX;
  delta.y = deltaY;
  delta = delta.matrixTransform(matrix);
  return {
    x: delta.x - origin.x,
    y: delta.y - origin.y,
  };
}

function getNormalizedPoint(x, y, svgElement) {
  const ctm = svgElement.getScreenCTM();
  const point = svgElement.createSVGPoint();
  point.x = x;
  point.y = y;
  return point.matrixTransform(ctm.inverse());
}

function getLineDistance(x1, y1, x2, y2) {
  const xdiff = x2 - x1;
  const ydiff = y2 - y1;
  return Math.sqrt(xdiff * xdiff + ydiff * ydiff);
}

function getMultiTouchDistance(touches) {
  return getLineDistance(
    touches[0].clientX,
    touches[0].clientY,
    touches[1].clientX,
    touches[1].clientY
  );
}

function adjustScale(scaleX, scaleY, minScale, maxScale, initialSvgViewBox) {
  const result = {
    adjustedScaleX: scaleX,
    adjustedScaleY: scaleY,
  };

  const verifyMaxScale = (scale, max) => {
    if (scale > max) {
      const adjustmentFactor = max / scale;
      result.adjustedScaleX *= adjustmentFactor;
      result.adjustedScaleY *= adjustmentFactor;
    }
  };

  const verifyMinScale = (scale, min) => {
    if (scale < min) {
      const adjustmentFactor = min / scale;
      result.adjustedScaleX *= adjustmentFactor;
      result.adjustedScaleY *= adjustmentFactor;
    }
  };

  verifyMinScale(result.adjustedScaleX, initialSvgViewBox.scaleX * minScale);
  verifyMinScale(result.adjustedScaleY, initialSvgViewBox.scaleY * minScale);
  verifyMaxScale(result.adjustedScaleX, initialSvgViewBox.scaleX * maxScale);
  verifyMaxScale(result.adjustedScaleY, initialSvgViewBox.scaleY * maxScale);

  return result;
}

function panSvgViewBox(svgElement, svgViewBox, deltaX, deltaY, panStart) {
  const normalizedDelta = getNormalizedDelta(
    -deltaX,
    -deltaY,
    panStart.matrix,
    svgElement
  );

  return {
    ...svgViewBox,
    x: panStart.orig.x + normalizedDelta.x,
    y: panStart.orig.y + normalizedDelta.y,
  };
}

function scaleSvgViewBox(
  svgViewBox,
  svgElement,
  initialSvgViewBox,
  svgViewPort,
  minScale,
  maxScale,
  newScaleX,
  newScaleY,
  center
) {
  const { adjustedScaleX, adjustedScaleY } = adjustScale(
    newScaleX,
    newScaleY,
    minScale,
    maxScale,
    initialSvgViewBox
  );

  if (
    adjustedScaleX === svgViewBox.scaleX &&
    adjustedScaleY === svgViewBox.scaleY
  ) {
    return null;
  }

  let x;
  let y;
  if (center != null) {
    const normalizedCenterPoint = getNormalizedPoint(
      center.x,
      center.y,
      svgElement
    );

    const distanceFromCenterToOrigin = {
      x: normalizedCenterPoint.x - svgViewBox.x,
      y: normalizedCenterPoint.y - svgViewBox.y,
    };
    const pixelDistanceFromCenterToViewBoxOrigin = {
      x: distanceFromCenterToOrigin.x * svgViewBox.scaleX,
      y: distanceFromCenterToOrigin.y * svgViewBox.scaleY,
    };
    const pixelDistanceFromCenterToNewScaleOrigin = {
      x: distanceFromCenterToOrigin.x * adjustedScaleX,
      y: distanceFromCenterToOrigin.y * adjustedScaleY,
    };
    const pixelDistanceDiffFromCenterToOrigin = {
      x:
        pixelDistanceFromCenterToNewScaleOrigin.x -
        pixelDistanceFromCenterToViewBoxOrigin.x,
      y:
        pixelDistanceFromCenterToNewScaleOrigin.y -
        pixelDistanceFromCenterToViewBoxOrigin.y,
    };
    const distanceDiffFromCenterToOrigin = {
      x: pixelDistanceDiffFromCenterToOrigin.x / adjustedScaleX,
      y: pixelDistanceDiffFromCenterToOrigin.y / adjustedScaleY,
    };

    x = svgViewBox.x + distanceDiffFromCenterToOrigin.x;
    y = svgViewBox.y + distanceDiffFromCenterToOrigin.y;
  } else {
    x =
      svgViewBox.x -
      (svgViewPort.width / adjustedScaleX -
        svgViewPort.width / svgViewBox.scaleX) /
        2;
    y =
      svgViewBox.y -
      (svgViewPort.height / adjustedScaleY -
        svgViewPort.height / svgViewBox.scaleY) /
        2;
  }

  return { x, y, scaleX: adjustedScaleX, scaleY: adjustedScaleY };
}

const MOUSE_WHEEL_MODIFIER = {
  0: 0.01, // deltaMode.DOM_DELTA_PIXEL
  1: 0.15, // deltaMode.DOM_DELTA_LINE
};

const MAX_MOUSE_WHEEL_SCALE_FACTOR = 2;

function clearAllClasses(className, svgElement) {
  Array.from(svgElement.getElementsByClassName(className)).forEach(
    (element) => {
      removeClassSvg(element, className);
    }
  );
}

function addClass(recordId, className, map) {
  const element = map.get(recordId);
  if (element) {
    addClassSvg(element, className);
  }
}

function removeClass(recordId, className, map) {
  const element = map.get(recordId);
  if (element) {
    removeClassSvg(element, className);
  }
}

function addClassSvg(element, className) {
  const currentClasses = element.getAttribute("class");
  element.setAttribute("class", currentClasses + " " + className);
}

function removeClassSvg(element, className) {
  const currentClasses = element.getAttribute("class");
  let currentClassesArray = currentClasses.split(/\s/);
  currentClassesArray = currentClassesArray.filter(
    (item) => item !== className
  );
  if (currentClassesArray.length !== 0) {
    element.setAttribute("class", currentClassesArray.join(" "));
  }
}

function getPropValue(obj, propName) {
  return obj[propName] || "";
}

function calcSvgScale(svgElement, svgViewPort, preservePinSizeRatio) {
  if (preservePinSizeRatio) return 1;
  const widthScale = svgElement.viewBox.baseVal.width / svgViewPort.width;
  const heightScale = svgElement.viewBox.baseVal.height / svgViewPort.height;
  return widthScale > heightScale ? widthScale : heightScale;
}

function calculateSpaceCentroid(points) {
  let x = 0;
  let y = 0;
  let signedArea = 0;
  let i, j, f, point1, point2;

  for (
    i = 0, j = points.numberOfItems - 1;
    i < points.numberOfItems;
    j = i, i++
  ) {
    point1 = points.getItem(j);
    point2 = points.getItem(i);
    f = point1.x * point2.y - point2.x * point1.y;
    signedArea += f;
    x += (point1.x + point2.x) * f;
    y += (point1.y + point2.y) * f;
  }

  signedArea /= 2;

  return {
    x: x / (signedArea * 6),
    y: y / (signedArea * 6),
  };
}

function setTriRecordIdOnPin(node, triRecordId, idAttribute ) {
  if (!node || !triRecordId) return;
  node.setAttribute( idAttribute, triRecordId);
  node.setAttribute("pin", "true");
  if (node.children.length > 0) {
    Array.from(node.children).forEach((node) => {
      setTriRecordIdOnPin( node, triRecordId, idAttribute );
    });
  }
}

export default {
  getSvgExtents,
  setSvgViewBox,
  updateSvgStrokeWidths,
  createInitialSvgViewBox,
  createAttachedMap,
  createInteractiveMap,
  getNormalizedDelta,
  getNormalizedPoint,
  getLineDistance,
  getMultiTouchDistance,
  MOUSE_WHEEL_MODIFIER,
  MAX_MOUSE_WHEEL_SCALE_FACTOR,
  adjustScale,
  scaleSvgViewBox,
  panSvgViewBox,
  clearAllClasses,
  addClass,
  removeClass,
  addClassSvg,
  removeClassSvg,
  getPropValue,
  calcSvgScale,
  calculateSpaceCentroid,
  setTriRecordIdOnPin,
  isSvgViewBoxEquals,
  computeCurrentSvgViewBoxScale,
};
